// QueryClient comes from query-core (added as a direct dep) because Bun's
// bundler doesn't follow react-query's `export * from "@tanstack/query-core"`
// re-export chain — the symbol is missing from the bundled namespace if
// imported via @tanstack/react-query.
import { QueryCache, QueryClient } from "@tanstack/query-core";
import { QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode } from "react";
import { ApiError } from "@/lib/api/_client";
import { log } from "@/lib/logger/browser-logger";

// Central observer: every query failure in the app is logged here through the
// browser logger (which POSTs to /internal/client-logs), so no individual query
// needs its own catch/log.
const queryCache = new QueryCache({
  onError: (error, query) => {
    log.error({ err: error, queryKey: query.queryKey }, "Query failed");
  },
});

// 4xx responses (404, 422, 429, ...) can never succeed on retry — retrying
// them only drains the rate-limit budget (see the 2026-08-05 429 lockout).
export function isPermanentQueryError(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 400 && error.status < 500;
}

// Everything else (5xx, network errors) keeps react-query's default of 3.
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (isPermanentQueryError(error)) {
    return false;
  }
  return failureCount < 3;
}

const queryClient = new QueryClient({
  queryCache,
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
  },
});

export function QueryProvider({ children }: { children: ReactNode }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
